import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, Check } from 'lucide-react';
import { useUpdateProfileMutation } from '@/features/settings/profile-api';
import { HintTooltip } from '@/components/ui/HintTooltip';
import { cn } from '@/lib/cn';
import { useUIStore } from '@/lib/store';

/*
 * LanguageSwitcher (Sessio 17; persistointikorjaus 18.7.2026).
 *
 * Header:in kielenvaihtaja. Suomi <-> English. Vaihto:
 *  1) useUIStore.setLocale(locale) - persistoituu localStorageen (luviamo-ui-prefs) ja
 *     i18n.ts:n store-subscribe levittaa i18nextiin -> UI vaihtuu valittomasti JA valinta
 *     sailyy sivunlatauksen/OAuth-redirectin yli. (Aiempi suora i18n.changeLanguage ohitti
 *     storen -> alkulataus pakotti kielen takaisin storen 'fi'-arvoon.)
 *  2) PATCH /api/me/profile { locale } - tallennetaan users.locale-sarakkeeseen
 *     (best-effort; server-locale sovelletaan bootstrapissa useServerLocaleSync-hookilla).
 *
 * Toteutettu Header:n user-menun patternilla (T9-saasto, ei Radix-pakettia).
 */

const LANGUAGES = [
  { value: 'fi' as const, label: 'Suomi' },
  { value: 'en' as const, label: 'English' },
];

export function LanguageSwitcher() {
  const { i18n, t } = useTranslation();
  const updateMutation = useUpdateProfileMutation();
  const setLocale = useUIStore((s) => s.setLocale);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const currentLocale: 'fi' | 'en' = i18n.language.startsWith('en') ? 'en' : 'fi';

  useEffect(() => {
    if (!open) return;
    function onClick(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleSelect = (locale: 'fi' | 'en') => {
    setOpen(false);
    if (locale === currentLocale) return;
    // Storen kautta (persistoituu + i18n-synkka i18n.ts:ssa) -- EI suoraan i18n:aan.
    setLocale(locale);
    // best-effort tallennus serverille; epaonnistuminen ei estä paikallista vaihtoa
    updateMutation.mutate({ locale });
  };

  return (
    <div ref={containerRef} className="relative" data-testid="language-switcher">
      <HintTooltip content={t('hints.header.language')} side="bottom">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label={t('header.languageSwitcher.label', { defaultValue: 'Vaihda kieli' })}
          aria-haspopup="menu"
          aria-expanded={open}
          className="flex h-8 items-center justify-center gap-1 rounded-sm px-1.5 text-metsa hover:bg-usva focus-ring"
        >
          <Globe className="h-4 w-4" aria-hidden="true" />
          {/* Nakyva kielikoodi: pelkka maapallo ei kerro etta tasta vaihdetaan kieli (Petri 18.7.) */}
          <span className="text-xs font-medium uppercase" data-testid="language-switcher-current">
            {currentLocale}
          </span>
        </button>
      </HintTooltip>
      {open && (
        <div
          role="menu"
          aria-label={t('header.languageSwitcher.menuLabel', { defaultValue: 'Valitse kieli' })}
          className="absolute right-0 top-9 z-30 w-40 overflow-hidden rounded-sm border border-kaste bg-white shadow-lg"
        >
          {LANGUAGES.map((lang) => {
            const isSelected = lang.value === currentLocale;
            return (
              <button
                key={lang.value}
                type="button"
                role="menuitemradio"
                aria-checked={isSelected}
                onClick={() => handleSelect(lang.value)}
                data-testid={`language-switcher-option-${lang.value}`}
                className={cn(
                  'flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm text-teksti hover:bg-usva focus-ring',
                )}
              >
                <span>{lang.label}</span>
                {isSelected && <Check className="h-4 w-4 text-sammal" aria-hidden="true" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
